import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _taggedTemplateLiteralLoose from "@babel/runtime/helpers/esm/taggedTemplateLiteralLoose";

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  white-space: nowrap;\n  box-sizing: border-box;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  flex-direction: row;\n  border: none;\n  cursor: ", ";\n  outline: none;\n  margin: 0;\n  background-color: transparent;\n  & > ", " {\n    transition: color 0.1s ease-out;\n    color: ", ";\n  }\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

/* eslint-env browser */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { H5 } from '../../Typography';
import { ADVENTURE_GREEN, ADVENTURE_GREEN_FOCUSED, ELEMENT_GRAY, NIGHTINGALE_BLACK } from '../../colors';
import { prevent } from '../../helpers';
import { useDeviceType } from '../../hooks';
import { TABS_BUTTON_BACKDROP_SIZE, TABS_BUTTON_BOTTOM_PADDING, TABS_BUTTON_HORIZONTAL_PADDING, TABS_BUTTON_TOP_PADDING } from '../constants';
import { keycodes } from '../../helpers/keyboard';
import { baseHighlightStyle } from '../../tokens/highlight';
import { paddingLeft } from '../../style/padding-left';
import { paddingRight } from '../../style/padding-right';
import { paddingBottom } from '../../style/padding-bottom';
import { paddingTop } from '../../style/padding-top';
var TabButtonComponent = styled.button(_templateObject(), function (_ref) {
  var disabled = _ref.disabled;
  return disabled ? 'default' : 'pointer';
}, H5, function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled ? ELEMENT_GRAY : NIGHTINGALE_BLACK;
}, function (_ref3) {
  var disabled = _ref3.disabled,
      $active = _ref3.$active,
      $hasTouch = _ref3.$hasTouch;
  return !disabled && !$active && !$hasTouch ? "\n        &:hover > " + H5 + ", &:focus > " + H5 + " {\n          color: " + ADVENTURE_GREEN + ";\n        }\n        &:active > " + H5 + " {\n          color: " + ADVENTURE_GREEN_FOCUSED + ";\n        }\n      " : '';
}, function (_ref4) {
  var $fullWidth = _ref4.$fullWidth;
  return $fullWidth ? "\n        flex-grow: 1; \n        flex-basis: 0;\n      " : '';
}, function (_ref5) {
  var $last = _ref5.$last,
      $paddingOuterHorizontal = _ref5.$paddingOuterHorizontal,
      $paddingInnerHorizontal = _ref5.$paddingInnerHorizontal;
  return $last ? paddingRight($paddingOuterHorizontal) : paddingRight($paddingInnerHorizontal);
}, function (_ref6) {
  var $first = _ref6.$first,
      $paddingOuterHorizontal = _ref6.$paddingOuterHorizontal,
      $paddingInnerHorizontal = _ref6.$paddingInnerHorizontal;
  return $first ? paddingLeft($paddingOuterHorizontal) : paddingLeft($paddingInnerHorizontal);
}, function (_ref7) {
  var $paddingTop = _ref7.$paddingTop;
  return paddingTop($paddingTop != null ? $paddingTop : TABS_BUTTON_TOP_PADDING);
}, function (_ref8) {
  var $paddingBottom = _ref8.$paddingBottom;
  return paddingBottom($paddingBottom != null ? $paddingBottom : TABS_BUTTON_BOTTOM_PADDING);
}, baseHighlightStyle);

var TabButton = function TabButton(_ref9) {
  var badge = _ref9.badge,
      paddingTop = _ref9.paddingTop,
      paddingBottom = _ref9.paddingBottom,
      _ref9$paddingInnerHor = _ref9.paddingInnerHorizontal,
      paddingInnerHorizontal = _ref9$paddingInnerHor === void 0 ? TABS_BUTTON_HORIZONTAL_PADDING : _ref9$paddingInnerHor,
      _ref9$paddingOuterHor = _ref9.paddingOuterHorizontal,
      paddingOuterHorizontal = _ref9$paddingOuterHor === void 0 ? TABS_BUTTON_BACKDROP_SIZE : _ref9$paddingOuterHor,
      name = _ref9.name,
      _ref9$first = _ref9.first,
      first = _ref9$first === void 0 ? false : _ref9$first,
      _ref9$last = _ref9.last,
      last = _ref9$last === void 0 ? false : _ref9$last,
      _ref9$fullWidth = _ref9.fullWidth,
      fullWidth = _ref9$fullWidth === void 0 ? false : _ref9$fullWidth,
      selected = _ref9.selected,
      select = _ref9.select,
      focus = _ref9.focus,
      focusNext = _ref9.focusNext,
      focusPrevious = _ref9.focusPrevious,
      focusFirst = _ref9.focusFirst,
      focusLast = _ref9.focusLast,
      refs = _ref9.refs,
      refsInner = _ref9.refsInner,
      children = _ref9.children,
      props = _objectWithoutPropertiesLoose(_ref9, ["badge", "paddingTop", "paddingBottom", "paddingInnerHorizontal", "paddingOuterHorizontal", "name", "first", "last", "fullWidth", "selected", "select", "focus", "focusNext", "focusPrevious", "focusFirst", "focusLast", "refs", "refsInner", "children"]);

  // Docs: https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html
  var handleKeyPress = useCallback(function (e) {
    var keyCode = e.which.toString();

    if (e.target instanceof HTMLElement) {
      switch (keyCode) {
        case keycodes.HOME:
          {
            prevent(e);
            focusFirst();
            return false;
          }

        case keycodes.END:
          {
            prevent(e);
            focusLast();
            return false;
          }

        case keycodes.LEFT:
          {
            prevent(e);
            focusPrevious(name);
            return false;
          }

        case keycodes.RIGHT:
          {
            prevent(e);
            focusNext(name);
            return false;
          }
      }
    }
  }, [focusFirst, focusLast, focusPrevious, focusNext]);

  var _useDeviceType = useDeviceType(),
      hasTouchScreen = _useDeviceType.hasTouchScreen;

  var handleClick = useCallback(function () {
    select(name);
    focus(name);
  }, [name, select, focus]);
  var setButtonInnerRef = useCallback(function (ref) {
    refs.current[name] = ref;
  }, [name]);
  var setHeaderInnerRef = useCallback(function (ref) {
    refsInner.current[name] = ref;
  }, [name]);
  return /*#__PURE__*/React.createElement(TabButtonComponent, _extends({
    $first: first,
    $last: last,
    $fullWidth: fullWidth,
    $hasTouch: hasTouchScreen,
    $active: selected === name,
    $paddingTop: paddingTop,
    $paddingBottom: paddingBottom,
    $paddingInnerHorizontal: paddingInnerHorizontal,
    $paddingOuterHorizontal: paddingOuterHorizontal,
    ref: setButtonInnerRef,
    onClick: handleClick,
    onKeyDown: handleKeyPress
  }, props), /*#__PURE__*/React.createElement(H5, {
    as: 'span',
    ref: setHeaderInnerRef
  }, children), badge ? /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0", badge) : null);
};

export default TabButton;